import { useState, useEffect, useRef, useMemo } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { Menu, X, LogOut, Bell, Moon, Sun, Wifi, CreditCard, Users, Wallet, User, LayoutDashboard, Globe, Heart, ChevronDown } from "lucide-react";
import CreateHotspotModal from "@/components/CreateHotspotModal";
import notificationService from "@/api/services/notificationService";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";

export default function EndUserDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, theme, toggleTheme } = useApp();
  const { i18n: i18nHook } = useTranslation();
  const [langOverride, setLangOverride] = useState(
    localStorage.getItem("i18nextLng")?.startsWith("fr") ? "fr" : "en"
  );
  const t = i18nHook.getFixedT(langOverride);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [dragPosition, setDragPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef(0);
  const [language, setLanguage] = useState<"EN" | "FR">(
    i18n.resolvedLanguage?.startsWith("fr") ? "FR" : "EN"
  );

  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng?.startsWith("fr") ? "FR" : "EN");
    };
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  useEffect(() => {
    const storedLang = localStorage.getItem("i18nextLng");
    if (storedLang) {
      const normalized = storedLang.startsWith("fr") ? "fr" : "en";
      setLangOverride(normalized);
      if (i18nHook.language !== normalized) {
        i18nHook.changeLanguage(normalized);
      }
    }
    const onAppLanguageChanged = (event: Event) => {
      const next = (event as CustomEvent).detail as string;
      const normalized = next?.startsWith("fr") ? "fr" : "en";
      setLangOverride(normalized);
    };
    window.addEventListener("appLanguageChanged", onAppLanguageChanged as EventListener);
    return () => {
      window.removeEventListener("appLanguageChanged", onAppLanguageChanged as EventListener);
    };
  }, [i18nHook]);

  const topMenuItems = useMemo(
    () => [
      {
        label: t("enduser.sidebar.dashboard", { defaultValue: "Dashboard" }),
        path: "/Dashboardoverview",
        icon: LayoutDashboard,
        matchPaths: ["/Dashboardoverview", "/user"],
      },
      {
        label: t("enduser.sidebar.hotspots", { defaultValue: "Hotspots" }),
        path: "/Hotspots",
        icon: Wifi,
        matchPaths: ["/Hotspots", "/user/hotspots"],
      },
      {
        label: t("enduser.sidebar.plans", { defaultValue: "Plans" }),
        path: "/PricingPlan",
        icon: CreditCard,
        matchPaths: ["/PricingPlan", "/user/pricing"],
      },
      {
        label: t("enduser.sidebar.subscriptions", { defaultValue: "Subscriptions" }),
        path: "/Subscription",
        icon: Users,
        matchPaths: ["/Subscription", "/user/subscription"],
      },
      {
        label: t("enduser.sidebar.wallet", { defaultValue: "Wallet" }),
        path: "/Wallet",
        icon: Wallet,
        matchPaths: ["/Wallet", "/user/wallet"],
      },
    ],
    [t]
  );
  const bottomMenuItems = useMemo(
    () => [
      {
        label: t("enduser.sidebar.profile", { defaultValue: "Profile" }),
        path: "/user/profile",
        icon: User,
        matchPaths: ["/user/profile"],
      },
      {
        label: t("enduser.sidebar.notification", { defaultValue: "Notification" }),
        path: "/Notification",
        icon: Bell,
        matchPaths: ["/Notification", "/user/notifications"],
      },
    ],
    [t]
  );
  // Donate menu — re-enabled now that backend is live.
  // Endpoints: /api/app/donate/ngos, /api/app/donate/ngo, /api/app/donate/ngo/verify, /api/app/donate/top-donators
  const donateMenuItems = useMemo(
    () => [
      {
        label: t("enduser.sidebar.donate", { defaultValue: "Donate" }),
        path: "/user/donate",
        matchPaths: ["/user/donate", "/Donate"],
      },
      {
        label: t("enduser.sidebar.supportDonate", { defaultValue: "Support an NGO" }),
        path: "/user/support-donate",
        matchPaths: ["/user/support-donate", "/SupportDonate"],
      },
    ],
    [t]
  );
  const isDonateMenuActive = donateMenuItems.some((item) => item.matchPaths.includes(location.pathname));
  const [isDonateDropdownOpen, setIsDonateDropdownOpen] = useState(isDonateMenuActive);

  useEffect(() => {
    if (isDonateMenuActive) {
      setIsDonateDropdownOpen(true);
    }
  }, [isDonateMenuActive]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const syncUnreadCount = async () => {
      try {
        const response = await notificationService.getNotifications({ page: 1, limit: 1 });
        setUnreadNotificationCount(response.unreadCount || 0);
      } catch {
        setUnreadNotificationCount(0);
      }
    };

    const handleUnreadCountChanged = (event: Event) => {
      const nextCount = Number((event as CustomEvent).detail);
      if (Number.isFinite(nextCount)) {
        setUnreadNotificationCount(Math.max(0, nextCount));
      }
    };

    void syncUnreadCount();
    window.addEventListener("notificationsUnreadCountChanged", handleUnreadCountChanged as EventListener);

    return () => {
      window.removeEventListener("notificationsUnreadCountChanged", handleUnreadCountChanged as EventListener);
    };
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    dragStartX.current = e.touches[0].clientX;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile || !isDragging) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - dragStartX.current;
    
    if (sidebarOpen) {
      if (diff < 0) {
        setDragPosition(Math.max(diff, -256));
      }
    } else {
      if (dragStartX.current < 20 && diff > 0) {
        setDragPosition(Math.min(diff - 256, 0));
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging) return;
    setIsDragging(false);
    
    if (Math.abs(dragPosition) > 128) {
      setSidebarOpen(!sidebarOpen);
    }
    
    setDragPosition(0);
  };

  const handleHotspotCreated = (hotspot: Record<string, unknown>) => {
    console.log("Hotspot created successfully!", hotspot);
    window.dispatchEvent(new CustomEvent('hotspotCreated', { detail: hotspot }));
  };

  function getInitials(name?: string, email?: string): string {
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
      } else if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
    }
    
    if (email) {
      const emailPrefix = email.split('@')[0];
      const words = emailPrefix.match(/[A-Za-z]+/g) || [];
      if (words.length >= 2) {
        return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
      }
      return emailPrefix.substring(0, 2).toUpperCase();
    }
    
    return 'U';
  }

  return (
    <div className={`flex h-screen overflow-hidden ${theme === 'dark' ? 'bg-black' : 'bg-background'}`}>
      <CreateHotspotModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleHotspotCreated}
      />

      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        ref={sidebarRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: isMobile && isDragging ? `translateX(${dragPosition}px)` : undefined,
          width: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
          minWidth: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
          maxWidth: isMobile ? '256px' : sidebarOpen ? '256px' : '80px',
        }}
        className={`
          ${isMobile ? 'fixed' : 'relative'}
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${theme === 'dark' ? 'bg-[#333436] border-[#2b2b2c]' : 'bg-gray-100 border-gray-200'}
          border-r flex flex-col h-full z-50
          ${isMobile ? 'top-0 left-0' : ''}
        `}
      >
        <div className={`p-4 ${sidebarOpen || isMobile ? 'h-[76px]' : 'h-[76px]'}`}>
          <div className="flex items-center justify-between h-full">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center w-full'} focus:outline-none hover:opacity-80`}
            >
              {(sidebarOpen || isMobile) ? (
                <img
                  src={theme === 'dark' ? '/KonnectXLogo-dark.png' : '/KonnectXLogo-light.png'}
                  alt="KonectX Logo"
                  className="h-10 object-contain cursor-pointer"
                />
              ) : (
                <img
                  src={theme === 'dark' ? '/icons/favicon-white.png' : '/icons/favicon-blue.png'}
                  alt="KonectX Icon"
                  className="w-12 h-12 object-contain cursor-pointer"
                />
              )}
            </button>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(false)}
                className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
              >
                <X size={20} className={theme === 'dark' ? 'text-white' : 'text-gray-800'} />
              </button>
            )}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {topMenuItems.map((item) => {
            const isActive = item.matchPaths.includes(location.pathname);
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setSidebarOpen(false);
                }}
                className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                      : 'text-gray-700 hover:bg-blue-600 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{item.label}</span>}
              </button>
            );
          })}

          <button
            onClick={() => {
              if (!(sidebarOpen || isMobile)) {
                navigate("/Donate");
                return;
              }
              setIsDonateDropdownOpen((prev) => !prev);
            }}
            className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
              isDonateMenuActive
                ? 'bg-blue-600 text-white'
                : theme === 'dark'
                  ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                  : 'text-gray-700 hover:bg-blue-600 hover:text-white'
            }`}
          >
            <Heart className="w-5 h-5 flex-shrink-0" />
            {(sidebarOpen || isMobile) && (
              <>
                <span className="font-medium text-base whitespace-nowrap flex-1 text-left">
                  {t("enduser.sidebar.donate", { defaultValue: "Donate" })}
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${isDonateDropdownOpen ? "rotate-180" : ""}`}
                />
              </>
            )}
          </button>

          {(sidebarOpen || isMobile) && isDonateDropdownOpen && (
            <div className="space-y-1">
              {donateMenuItems.map((item) => {
                const isActive = item.matchPaths.includes(location.pathname);
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : theme === "dark"
                          ? "text-gray-300 hover:bg-blue-600 hover:text-white"
                          : "text-gray-700 hover:bg-blue-600 hover:text-white"
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-current opacity-90 flex-shrink-0" />
                    <span className="font-medium text-base whitespace-nowrap">{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {bottomMenuItems.map((item) => {
            const isActive = item.matchPaths.includes(location.pathname);
            const showUnreadBadge = item.icon === Bell && unreadNotificationCount > 0;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setSidebarOpen(false);
                }}
                className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : theme === 'dark'
                      ? 'text-gray-300 hover:bg-blue-600 hover:text-white'
                      : 'text-gray-700 hover:bg-blue-600 hover:text-white'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <item.icon className="w-5 h-5" />
                  {showUnreadBadge && (
                    <span className="absolute -top-2 -right-2 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-red-500 text-white text-[10px] leading-none font-bold flex items-center justify-center">
                      {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
                    </span>
                  )}
                </div>
                {(sidebarOpen || isMobile) && <span className="font-medium text-base whitespace-nowrap">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className={`border-t ${theme === 'dark' ? 'border-gray-800' : 'border-gray-300'}`}>
          <div className="p-4 space-y-2">
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-800' 
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              {theme === "light" ? <Moon className="w-5 h-5 flex-shrink-0" /> : <Sun className="w-5 h-5 flex-shrink-0" />}
              {(sidebarOpen || isMobile) && (
                <span className="font-medium text-base whitespace-nowrap">
                  {theme === "light"
                    ? t("enduser.sidebar.darkMode", { defaultValue: "Dark Mode" })
                    : t("enduser.sidebar.lightMode", { defaultValue: "Light Mode" })}
                </span>
              )}
            </button>
          
            <button
              onClick={logout}
              className={`w-full flex items-center ${sidebarOpen || isMobile ? 'gap-3' : 'justify-center'} px-4 py-3 rounded-lg hover:text-red-500 ${
                theme === 'dark' 
                  ? 'text-gray-300 hover:bg-gray-800' 
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {(sidebarOpen || isMobile) && (
                <span className="font-medium text-base whitespace-nowrap">
                  {t("enduser.sidebar.logout", { defaultValue: "Logout" })}
                </span>
              )}
            </button>
          </div>

          {(sidebarOpen || isMobile) && user && (
             <Link to="/operator/Settings" className="block">
            <div  className={`p-4 border-t ${theme === 'dark' ? 'border-gray-800' : 'border-gray-300'}`}>
              <div onClick={()=> {
              window.location.href = '/user/profile';
            }} className="flex cursor-pointer items-center gap-3">
                {(() => {
                  const displayInitials =
                    user.initials ||
                    getInitials(
                      user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name,
                      user.email
                    );
                  return user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={`${user.displayName || user.name || "User"} profile`}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                      {displayInitials}
                    </div>
                  );
                })()}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold truncate ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
                    {user.displayName || user.name || "User"}
                  </p>
                  <p className={`text-xs truncate ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {user.email}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const next = language === "EN" ? "fr" : "en";
                    localStorage.setItem("i18nextLng", next);
                    i18n.changeLanguage(next);
                    window.dispatchEvent(new CustomEvent("appLanguageChanged", { detail: next }));
                  }}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-colors ${
                    theme === 'dark'
                      ? 'bg-[#3a3c3f] border-[#555] text-gray-200 hover:bg-[#444648]'
                      : 'bg-white border-[#d1d5db] text-[#374151] hover:bg-gray-50'
                  }`}
                  aria-label={`Switch language to ${language === "EN" ? "French" : "English"}`}
                >
                  <Globe className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold tracking-wide">{language}</span>
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate("/Notification");
                    if (isMobile) setSidebarOpen(false);
                  }}
                  className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
                    theme === 'dark'
                      ? 'bg-[#3a3c3f] border-[#555] text-gray-200 hover:bg-[#444648]'
                      : 'bg-white border-[#d1d5db] text-[#374151] hover:bg-gray-50'
                  }`}
                  aria-label={t("enduser.sidebar.notification", { defaultValue: "Notification" })}
                >
                  <Bell className="w-4 h-4" />
                  {unreadNotificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[1rem] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-none font-bold flex items-center justify-center">
                      {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
            </Link>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden w-full">
        {isMobile && (
          <header className={`px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between relative ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 rounded-lg transition mr-2 ${
                theme === 'dark' ? 'hover:bg-gray-900 text-white' : 'hover:bg-accent text-foreground'
              }`}
            >
              <Menu size={24} />
            </button>
          </header>
        )}

       <main className={`flex-1 overflow-auto ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
          <Outlet />
              </main>
      </div>
    </div>
  );
}
